import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../components/auth/AuthContext";
import { HeaderBar } from "../components/layout/TopBar";
import NavBar from "../components/layout/NavBar";
import { BellArt } from "../components/adminSurface/heroArt";
import { Hero, SegControl, EmptyState } from "../components/adminSurface/AdminSurface";
import WorkflowDesigner from "../components/workflow/WorkflowDesigner";
import "../styles/workflow.css";
import { getMyTasks, getWorkflowTask, claimWorkflowTask, completeWorkflowTask } from "../api/workflowApi";

/**
 * My Tasks — the approver's page, and the only workflow surface an ordinary user needs.
 *
 * It is NOT under /admin on purpose. Designing workflows is an administrator function; deciding on one that
 * was assigned to you is just your job. A lawyer or a branch manager should never need admin rights to say yes.
 *
 * Deciding shows the case's own diagram, drawn by the same layout code as the designer, so an approver can see
 * where their step sits and what happens next before they commit to an answer.
 */
export default function MyTasksPage() {
  const { t } = useTranslation();
  const { token } = useAuth();

  const [filter, setFilter] = useState("open");
  const [tasks, setTasks] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const fail = useCallback((e) => {
    setError(e?.message || String(e));
    setNotice(null);
  }, []);

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const rows = await getMyTasks(token, filter === "all");
      setTasks(Array.isArray(rows) ? rows : []);
      setError(null);
    } catch (e) {
      fail(e);
    }
  }, [token, filter, fail]);

  useEffect(() => {
    load();
  }, [load]);

  const loadDetail = useCallback(
    async (id) => {
      if (!id) {
        setDetail(null);
        return;
      }
      try {
        setDetail(await getWorkflowTask(token, id));
        setNote("");
        setError(null);
      } catch (e) {
        fail(e);
      }
    },
    [token, fail]
  );

  useEffect(() => {
    loadDetail(selectedId);
  }, [selectedId, loadDetail]);

  const act = useCallback(
    async (fn, successMsg) => {
      setBusy(true);
      try {
        await fn();
        setNotice(successMsg);
        setError(null);
        await load();
        await loadDetail(selectedId);
      } catch (e) {
        fail(e);
      } finally {
        setBusy(false);
      }
    },
    [load, loadDetail, selectedId, fail]
  );

  const onClaim = () => act(() => claimWorkflowTask(token, selectedId), t("workflow.msg.claimed", { defaultValue: "This task is now yours." }));

  const onDecide = (decision) =>
    act(
      () => completeWorkflowTask(token, selectedId, decision, note),
      decision === "Reject"
        ? t("workflow.msg.rejected", { defaultValue: "Recorded as rejected." })
        : t("workflow.msg.approved", { defaultValue: "Recorded." })
    );

  const overdueCount = useMemo(() => tasks.filter((x) => x.isOverdue).length, [tasks]);
  const task = detail?.task;
  const canAct = !!detail?.canAct;
  const isAck = task && detail?.nodes?.find((n) => (n.nodeKey ?? n.NodeKey) === task.nodeKey)?.nodeType === "Acknowledge";

  return (
    <>
      <HeaderBar />
      <NavBar />
      <div className="page-container">
        <Hero
          icon="✅"
          title={t("workflow.myTasks.title", { defaultValue: "My Tasks" })}
          subtitle={t("workflow.myTasks.subtitle", { defaultValue: "Documents waiting on your decision." })}
          statValue={String(tasks.filter((x) => x.isOpen).length)}
          statLabel={t("workflow.myTasks.waiting", { defaultValue: "waiting on you" })}
          statDotColor={overdueCount > 0 ? "#ef4444" : "#4ade80"}
          art={<BellArt />}
          onRefresh={load}
          refreshing={busy}
        />

        <div className="asf-card">
          <div className="wf-bar">
            <SegControl
              value={filter}
              onChange={setFilter}
              options={[
                { value: "open", label: t("workflow.myTasks.open", { defaultValue: "Waiting on me" }) },
                { value: "all", label: t("workflow.myTasks.all", { defaultValue: "Everything" }) },
              ]}
            />
            {overdueCount > 0 && (
              <span className="wf-pill is-rejected">
                {t("workflow.myTasks.overdue", { defaultValue: "{{n}} overdue", n: overdueCount })}
              </span>
            )}
          </div>

          {error && <div className="wf-warn">{error}</div>}
          {notice && <div className="wf-ok">{notice}</div>}

          <div className="wf-grid">
            <div className="wf-list">
              {tasks.length === 0 && (
                <EmptyState
                  icon="🎉"
                  title={t("workflow.myTasks.empty", { defaultValue: "Nothing waiting on you" })}
                  hint={t("workflow.myTasks.emptyHint", { defaultValue: "Tasks appear here when a workflow reaches a step assigned to you." })}
                />
              )}
              {tasks.map((x) => (
                <button
                  key={x.id}
                  className={`wf-list-item${selectedId === x.id ? " is-on" : ""}`}
                  onClick={() => setSelectedId(x.id)}
                >
                  <span>
                    <span className="wf-list-name">{x.name}</span>
                    <br />
                    <span className="wf-list-sub">
                      {x.subjectTitle || x.archiveNo || x.definitionName}
                      {x.isPoolTask && ` · ${t("workflow.myTasks.pool", { defaultValue: "unclaimed" })}`}
                    </span>
                  </span>
                  {x.isOverdue ? (
                    <span className="wf-pill is-rejected">{t("workflow.myTasks.overdueTag", { defaultValue: "overdue" })}</span>
                  ) : (
                    <span className={`wf-pill is-${String(x.status).toLowerCase()}`}>{x.status}</span>
                  )}
                </button>
              ))}
            </div>

            <div>
              {!detail ? (
                <EmptyState
                  icon="👈"
                  title={t("workflow.myTasks.pick", { defaultValue: "Pick a task" })}
                  hint={t("workflow.myTasks.pickHint", { defaultValue: "You will see where it sits in the workflow before you decide." })}
                />
              ) : (
                <>
                  <div className="wf-bar">
                    <strong>{task.name}</strong>
                    <span className="wf-pill is-running">{detail.instance?.definitionName}</span>
                    <div className="wf-bar-spacer" />
                    {task.dueAtUtc && (
                      <span className={`wf-pill ${task.isOverdue ? "is-rejected" : "is-draft"}`}>
                        {t("workflow.myTasks.due", { defaultValue: "due {{d}}", d: new Date(task.dueAtUtc).toLocaleString() })}
                      </span>
                    )}
                  </div>

                  {task.requireFourEyes && (
                    <div className="wf-note">
                      {t("workflow.myTasks.fourEyes", {
                        defaultValue:
                          "This step needs a second pair of eyes: whoever started this case, and the previous approver, cannot decide it.",
                      })}
                    </div>
                  )}

                  {task.isPoolTask && canAct && (
                    <div className="wf-note">
                      {t("workflow.myTasks.poolHint", {
                        defaultValue: "This is offered to your whole role. Take it to make it yours before deciding.",
                      })}
                    </div>
                  )}

                  {!canAct && task.isOpen && (
                    <div className="wf-warn">
                      {t("workflow.myTasks.cannotAct", { defaultValue: "This task is not assigned to you, so you cannot decide it." })}
                    </div>
                  )}

                  {!task.isOpen && (
                    <div className="wf-note">
                      {t("workflow.myTasks.alreadyDone", {
                        defaultValue: "Already {{d}} by {{who}}.",
                        d: task.decision || task.status,
                        who: task.completedBy || "—",
                      })}
                    </div>
                  )}

                  {canAct && task.isOpen && (
                    <div className="wf-decide">
                      <label className="wf-field">
                        <span>{t("workflow.myTasks.note", { defaultValue: "Note (optional)" })}</span>
                        <input value={note} onChange={(e) => setNote(e.target.value)} disabled={busy} />
                      </label>
                      <div className="wf-bar">
                        {task.isPoolTask && !task.claimedBy && (
                          <button className="asf-btn" onClick={onClaim} disabled={busy}>
                            {t("workflow.myTasks.claim", { defaultValue: "Take this task" })}
                          </button>
                        )}
                        {isAck ? (
                          <button className="asf-cta" onClick={() => onDecide("Acknowledge")} disabled={busy}>
                            {t("workflow.myTasks.acknowledge", { defaultValue: "Acknowledge" })}
                          </button>
                        ) : (
                          <>
                            <button className="asf-cta" onClick={() => onDecide("Approve")} disabled={busy}>
                              {t("workflow.myTasks.approve", { defaultValue: "Approve" })}
                            </button>
                            <button className="wf-reject" onClick={() => onDecide("Reject")} disabled={busy}>
                              {t("workflow.myTasks.reject", { defaultValue: "Reject" })}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  <WorkflowDesigner
                    nodes={detail.nodes ?? []}
                    edges={detail.edges ?? []}
                    tasks={detail.history ?? []}
                    instance={detail.instance}
                    readOnly
                    selectedKey={task.nodeKey}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
